import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Input,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Select,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  IconButton,
  Divider,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiType, FiLayout, FiBox, FiDroplet, FiGrid, FiSmartphone, FiBarChart2, FiSidebar, FiCode, FiColumns, FiExternalLink } from 'react-icons/fi';
import { FaRegNewspaper, FaRegSquare, FaColumns } from 'react-icons/fa';
import CustomCSSEditor from './CustomCSSEditor';
import ColumnLayoutManager from './ColumnLayoutManager';
import ContextualAdminLinks from './ContextualAdminLinks';
import { useClubTheme } from '../../contexts/ClubThemeContext';
import { FONT_PAIRINGS, loadGoogleFont } from '../../config/fonts';
import ColorPicker from '../common/ColorPicker';

interface VisualStylePanelProps {
  elementName: string;
  onStyleChange: (styles: Record<string, any>) => void;
  currentStyles?: Record<string, any>;
}

const VisualStylePanel: React.FC<VisualStylePanelProps> = ({
  elementName,
  onStyleChange,
  currentStyles = {},
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const clubTheme = useClubTheme();
  const primaryColor = clubTheme.primary || '#0b5cff';

  const [styles, setStyles] = useState({
    // Typography
    fontFamily: currentStyles.fontFamily || 'Inter',
    fontSize: currentStyles.fontSize || 16,
    fontWeight: currentStyles.fontWeight || 400,
    lineHeight: currentStyles.lineHeight || 1.5,
    letterSpacing: currentStyles.letterSpacing || 0,
    textTransform: currentStyles.textTransform || 'none',
    
    // Colors
    color: currentStyles.color || '#000000',
    backgroundColor: currentStyles.backgroundColor || '#ffffff',
    
    // Spacing
    paddingTop: currentStyles.paddingTop || 0,
    paddingRight: currentStyles.paddingRight || 0,
    paddingBottom: currentStyles.paddingBottom || 0,
    paddingLeft: currentStyles.paddingLeft || 0,
    marginTop: currentStyles.marginTop || 0,
    marginRight: currentStyles.marginRight || 0,
    marginBottom: currentStyles.marginBottom || 0,
    marginLeft: currentStyles.marginLeft || 0,
    
    // Layout
    width: currentStyles.width || 'auto',
    height: currentStyles.height || 'auto',
    display: currentStyles.display || 'block',
    
    // Grid Layout
    gridTemplateColumns: currentStyles.gridTemplateColumns || 'repeat(3, 1fr)',
    gridTemplateRows: currentStyles.gridTemplateRows || 'auto',
    gridColumnGap: currentStyles.gridColumnGap || 16,
    gridRowGap: currentStyles.gridRowGap || 16,
    gridAutoFlow: currentStyles.gridAutoFlow || 'row',
    alignItems: currentStyles.alignItems || 'stretch',
    justifyItems: currentStyles.justifyItems || 'stretch',
    
    // Custom CSS
    customCSS: currentStyles.customCSS || '',
    
    ...currentStyles,
  });

  // Sync local styles state when switching element or when parent provides updated styles
  useEffect(() => {
    setStyles({
      // Typography
      fontFamily: currentStyles.fontFamily || 'Inter',
      fontSize: currentStyles.fontSize || 16,
      fontWeight: currentStyles.fontWeight || 400,
      lineHeight: currentStyles.lineHeight || 1.5,
      letterSpacing: currentStyles.letterSpacing || 0,
      textTransform: currentStyles.textTransform || 'none',

      // Colors
      color: currentStyles.color || '#000000',
      backgroundColor: currentStyles.backgroundColor || '#ffffff',

      // Spacing
      paddingTop: currentStyles.paddingTop || 0,
      paddingRight: currentStyles.paddingRight || 0,
      paddingBottom: currentStyles.paddingBottom || 0,
      paddingLeft: currentStyles.paddingLeft || 0,
      marginTop: currentStyles.marginTop || 0,
      marginRight: currentStyles.marginRight || 0,
      marginBottom: currentStyles.marginBottom || 0,
      marginLeft: currentStyles.marginLeft || 0,

      // Layout
      width: currentStyles.width || 'auto',
      height: currentStyles.height || 'auto',
      display: currentStyles.display || 'block',

      // Grid Layout
      gridTemplateColumns: currentStyles.gridTemplateColumns || 'repeat(3, 1fr)',
      gridTemplateRows: currentStyles.gridTemplateRows || 'auto',
      gridColumnGap: currentStyles.gridColumnGap || 16,
      gridRowGap: currentStyles.gridRowGap || 16,
      gridAutoFlow: currentStyles.gridAutoFlow || 'row',
      alignItems: currentStyles.alignItems || 'stretch',
      justifyItems: currentStyles.justifyItems || 'stretch',

      // Custom CSS
      customCSS: currentStyles.customCSS || '',

      ...currentStyles,
    });
  }, [elementName, currentStyles]);

  const updateStyle = (key: string, value: any) => {
    const newStyles = { ...styles, [key]: value };
    setStyles(newStyles);
    onStyleChange(newStyles);
  };

  // Font pairing and curated font options
  const [pairingId, setPairingId] = useState<string>(FONT_PAIRINGS[0]?.id || '');
  const selectedPairing = useMemo(() => FONT_PAIRINGS.find(p => p.id === pairingId) || FONT_PAIRINGS[0], [pairingId]);
  const curatedFonts = useMemo(() => {
    const map = new Map<string, { name: string; googleFontsUrl: string }>();
    FONT_PAIRINGS.forEach(p => {
      map.set(p.heading, { name: p.heading, googleFontsUrl: p.googleFontsUrl });
      map.set(p.body, { name: p.body, googleFontsUrl: p.googleFontsUrl });
    });
    return Array.from(map.values());
  }, []);

  return (
    <Box
      width="280px"
      bg={bgColor}
      borderRight="1px"
      borderColor={primaryColor}
      height="100vh"
      overflowY="auto"
      pt="60px"
    >
      <Tabs size="sm" colorScheme="blue">
        <TabList px={2} flexWrap="wrap">
          <Tab><FiType /> <Text ml={1}>Obsah</Text></Tab>
          <Tab><FiLayout /> <Text ml={1}>Styl</Text></Tab>
          <Tab><FiColumns /> <Text ml={1}>Rozvržení</Text></Tab>
          <Tab><FiCode /> <Text ml={1}>CSS</Text></Tab>
          <Tab><FiExternalLink /> <Text ml={1}>Admin</Text></Tab>
        </TabList>

        <TabPanels>
          {/* Záložka: Obsah */}
          <TabPanel>
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="bold" fontSize="sm" textTransform="uppercase" color="gray.500">
                Typografie
              </Text>

              {/* Font pairing from Setup (curated) */}
              <FormControl>
                <FormLabel fontSize="xs">Párování fontů (Setup)</FormLabel>
                <HStack>
                  <Select
                    size="sm"
                    value={pairingId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setPairingId(id);
                      const p = FONT_PAIRINGS.find(pp => pp.id === id);
                      if (p) {
                        loadGoogleFont(p.googleFontsUrl);
                      }
                    }}
                  >
                    {FONT_PAIRINGS.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                </HStack>
                <HStack spacing={2} mt={2}>
                  <Button size="xs" variant="outline" onClick={() => { if (selectedPairing) { loadGoogleFont(selectedPairing.googleFontsUrl); updateStyle('fontFamily', selectedPairing.cssHeading); } }}>Použít nadpisový</Button>
                  <Button size="xs" variant="outline" onClick={() => { if (selectedPairing) { loadGoogleFont(selectedPairing.googleFontsUrl); updateStyle('fontFamily', selectedPairing.cssBody); } }}>Použít textový</Button>
                </HStack>
              </FormControl>

              {/* Curated font list (unique from pairing set) */}
              <FormControl>
                <FormLabel fontSize="xs">Dostupné fonty (Setup)</FormLabel>
                <Select
                  size="sm"
                  value={styles.fontFamily}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateStyle('fontFamily', val);
                    const found = FONT_PAIRINGS.find(p => p.cssHeading === val || p.cssBody === val);
                    if (found) loadGoogleFont(found.googleFontsUrl);
                  }}
                >
                  {curatedFonts.map(f => (
                    <option key={f.name} value={f.name}>{f.name}</option>
                  ))}
                </Select>
              </FormControl>

              {/* Rodina písma */}
              <FormControl>
                <FormLabel fontSize="xs">Písmo</FormLabel>
                <Select
                  size="sm"
                  value={styles.fontFamily}
                  onChange={(e) => updateStyle('fontFamily', e.target.value)}
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Lato">Lato</option>
                  <option value="Montserrat">Montserrat</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                </Select>
              </FormControl>

              {/* Velikost písma */}
              <FormControl>
                <FormLabel fontSize="xs">Velikost (px)</FormLabel>
                <HStack>
                  <NumberInput
                    size="sm"
                    value={styles.fontSize}
                    min={8}
                    max={128}
                    onChange={(_, val) => updateStyle('fontSize', val)}
                    flex={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </HStack>
              </FormControl>

              {/* Tloušťka písma */}
              <FormControl>
                <FormLabel fontSize="xs">Tloušťka</FormLabel>
                <HStack spacing={2}>
                  <Slider
                    value={styles.fontWeight}
                    min={100}
                    max={900}
                    step={100}
                    onChange={(val) => updateStyle('fontWeight', val)}
                    flex={1}
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg={primaryColor} />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="xs" minW="40px">{styles.fontWeight}</Text>
                </HStack>
              </FormControl>

              {/* Řádkování */}
              <FormControl>
                <FormLabel fontSize="xs">Řádkování</FormLabel>
                <HStack spacing={2}>
                  <Slider
                    value={styles.lineHeight}
                    min={0.5}
                    max={3}
                    step={0.1}
                    onChange={(val) => updateStyle('lineHeight', val)}
                    flex={1}
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg={primaryColor} />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="xs" minW="40px">{styles.lineHeight.toFixed(1)}</Text>
                </HStack>
              </FormControl>

              {/* Mezery mezi písmeny */}
              <FormControl>
                <FormLabel fontSize="xs">Mezera mezi písmeny (px)</FormLabel>
                <HStack spacing={2}>
                  <Slider
                    value={styles.letterSpacing}
                    min={-5}
                    max={10}
                    step={0.1}
                    onChange={(val) => updateStyle('letterSpacing', val)}
                    flex={1}
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg={primaryColor} />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="xs" minW="40px">{styles.letterSpacing.toFixed(1)}</Text>
                </HStack>
              </FormControl>

              {/* Transformace textu */}
              <FormControl>
                <FormLabel fontSize="xs">Transformace</FormLabel>
                <Select
                  size="sm"
                  value={styles.textTransform}
                  onChange={(e) => updateStyle('textTransform', e.target.value)}
                >
                  <option value="none">Žádné</option>
                  <option value="uppercase">VELKÁ PÍSMENA</option>
                  <option value="lowercase">malá písmena</option>
                  <option value="capitalize">První písmena velká</option>
                </Select>
              </FormControl>
            </VStack>
          </TabPanel>

          {/* Záložka: Styl */}
          <TabPanel>
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="bold" fontSize="sm" textTransform="uppercase" color="gray.500">
                Barvy
              </Text>

              {/* Barva textu */}
              <FormControl>
                <FormLabel fontSize="xs">Barva textu</FormLabel>
                <ColorPicker
                  value={styles.color}
                  onChange={(hex) => updateStyle('color', hex)}
                  recentStorageKey={`editor-text-${elementName}`}
                />
              </FormControl>

              {/* Barva pozadí */}
              <FormControl>
                <FormLabel fontSize="xs">Barva pozadí</FormLabel>
                <ColorPicker
                  value={styles.backgroundColor}
                  onChange={(hex) => updateStyle('backgroundColor', hex)}
                  recentStorageKey={`editor-bg-${elementName}`}
                />
              </FormControl>

              <Divider my={2} />

              <Text fontWeight="bold" fontSize="sm" textTransform="uppercase" color="gray.500">
                Odsazení a okraje
              </Text>

              {/* Vnitřní odsazení (padding) */}
              <FormControl>
                <FormLabel fontSize="xs">Vnitřní odsazení (px)</FormLabel>
                <VStack spacing={2}>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">N</Text>
                    <NumberInput
                      size="xs"
                      value={styles.paddingTop}
                      min={0}
                      onChange={(_, val) => updateStyle('paddingTop', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">P</Text>
                    <NumberInput
                      size="xs"
                      value={styles.paddingRight}
                      min={0}
                      onChange={(_, val) => updateStyle('paddingRight', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">D</Text>
                    <NumberInput
                      size="xs"
                      value={styles.paddingBottom}
                      min={0}
                      onChange={(_, val) => updateStyle('paddingBottom', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">L</Text>
                    <NumberInput
                      size="xs"
                      value={styles.paddingLeft}
                      min={0}
                      onChange={(_, val) => updateStyle('paddingLeft', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                </VStack>
              </FormControl>

              {/* Vnější okraj (margin) */}
              <FormControl>
                <FormLabel fontSize="xs">Vnější okraj (px)</FormLabel>
                <VStack spacing={2}>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">N</Text>
                    <NumberInput
                      size="xs"
                      value={styles.marginTop}
                      onChange={(_, val) => updateStyle('marginTop', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">P</Text>
                    <NumberInput
                      size="xs"
                      value={styles.marginRight}
                      onChange={(_, val) => updateStyle('marginRight', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">D</Text>
                    <NumberInput
                      size="xs"
                      value={styles.marginBottom}
                      onChange={(_, val) => updateStyle('marginBottom', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <HStack width="100%">
                    <Text fontSize="xs" minW="20px">L</Text>
                    <NumberInput
                      size="xs"
                      value={styles.marginLeft}
                      onChange={(_, val) => updateStyle('marginLeft', val)}
                      flex={1}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                </VStack>
              </FormControl>
            </VStack>
          </TabPanel>

          {/* Záložka: Rozvržení (mřížka) */}
          <TabPanel>
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="bold" fontSize="sm" textTransform="uppercase" color="gray.500">
                Mřížkové rozložení
              </Text>

              {/* Povolit mřížkové rozložení */}
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="xs" mb={0} flex={1}>Povolit mřížkové rozložení</FormLabel>
                <Switch
                  size="sm"
                  isChecked={styles.display === 'grid'}
                  onChange={(e) => updateStyle('display', e.target.checked ? 'grid' : 'block')}
                  sx={{
                    'span[data-checked]': {
                      bg: primaryColor,
                      borderColor: primaryColor,
                    }
                  }}
                />
              </FormControl>

              {styles.display === 'grid' && (
                <>
                  <Divider />

                  {/* Rychlé šablony */}
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="bold">Rychlé šablony</FormLabel>
                    <VStack spacing={2}>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '1fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FiSmartphone />
                          <Text>Jeden sloupec</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '1fr 1fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FaColumns />
                          <Text>Dva stejné (50 % / 50 %)</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '2fr 1fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FiBarChart2 />
                          <Text>Vlevo větší (66 % / 33 %)</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '1fr 2fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FiBarChart2 style={{ transform: 'scaleX(-1)' }} />
                          <Text>Vpravo větší (33 % / 66 %)</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '1fr 1fr 1fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FiGrid />
                          <Text>Tři stejné (33 % / 33 % / 33 %)</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '2fr 1fr 1fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FaRegNewspaper />
                          <Text>Zvýrazněný + dva (50 % / 25 % / 25 %)</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', 'repeat(4, 1fr)')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FaRegSquare />
                          <Text>Čtyři stejné (25 % každá)</Text>
                        </HStack>
                      </Button>
                      <Button
                        size="xs"
                        width="100%"
                        variant="outline"
                        onClick={() => updateStyle('gridTemplateColumns', '3fr 1fr')}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={2}>
                          <FiSidebar />
                          <Text>Hlavní + postranní (75 % / 25 %)</Text>
                        </HStack>
                      </Button>
                    </VStack>
                  </FormControl>

                  <Divider />

                  {/* Vlastní sloupce */}
                  <FormControl>
                    <FormLabel fontSize="xs">Sloupce mřížky</FormLabel>
                    <Input
                      size="sm"
                      value={styles.gridTemplateColumns}
                      onChange={(e) => updateStyle('gridTemplateColumns', e.target.value)}
                      placeholder="např. 1fr 2fr nebo 300px 1fr"
                      fontFamily="monospace"
                      fontSize="xs"
                    />
                    <Text fontSize="10px" color="gray.500" mt={1}>
                      Příklady: 1fr 1fr | 2fr 1fr | 200px 1fr | repeat(3, 1fr)
                    </Text>
                  </FormControl>

                  {/* Řádky mřížky */}
                  <FormControl>
                    <FormLabel fontSize="xs">Řádky mřížky</FormLabel>
                    <Input
                      size="sm"
                      value={styles.gridTemplateRows}
                      onChange={(e) => updateStyle('gridTemplateRows', e.target.value)}
                      placeholder="auto nebo 200px 1fr"
                      fontFamily="monospace"
                      fontSize="xs"
                    />
                  </FormControl>

                  {/* Mezera mezi sloupci */}
                  <FormControl>
                    <FormLabel fontSize="xs">Mezera mezi sloupci (px)</FormLabel>
                    <HStack spacing={2}>
                      <Slider
                        value={styles.gridColumnGap}
                        min={0}
                        max={100}
                        step={4}
                        onChange={(val) => updateStyle('gridColumnGap', val)}
                        flex={1}
                      >
                        <SliderTrack>
                          <SliderFilledTrack bg="purple.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                      <Text fontSize="xs" minW="40px">{styles.gridColumnGap}px</Text>
                    </HStack>
                  </FormControl>

                  {/* Mezera mezi řádky */}
                  <FormControl>
                    <FormLabel fontSize="xs">Mezera mezi řádky (px)</FormLabel>
                    <HStack spacing={2}>
                      <Slider
                        value={styles.gridRowGap}
                        min={0}
                        max={100}
                        step={4}
                        onChange={(val) => updateStyle('gridRowGap', val)}
                        flex={1}
                      >
                        <SliderTrack>
                          <SliderFilledTrack bg="purple.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                      <Text fontSize="xs" minW="40px">{styles.gridRowGap}px</Text>
                    </HStack>
                  </FormControl>

                  <Divider />

                  {/* Automatické rozmístění */}
                  <FormControl>
                    <FormLabel fontSize="xs">Automatické rozmístění</FormLabel>
                    <Select
                      size="sm"
                      value={styles.gridAutoFlow}
                      onChange={(e) => updateStyle('gridAutoFlow', e.target.value)}
                    >
                      <option value="row">Řádek (vodorovně)</option>
                      <option value="column">Sloupec (svisle)</option>
                      <option value="row dense">Řádek (zahuštěný)</option>
                      <option value="column dense">Sloupec (zahuštěný)</option>
                    </Select>
                  </FormControl>

                  {/* Zarovnání (vertikálně) */}
                  <FormControl>
                    <FormLabel fontSize="xs">Zarovnání prvků (vertikálně)</FormLabel>
                    <Select
                      size="sm"
                      value={styles.alignItems}
                      onChange={(e) => updateStyle('alignItems', e.target.value)}
                    >
                      <option value="stretch">Roztáhnout</option>
                      <option value="start">Začátek</option>
                      <option value="center">Střed</option>
                      <option value="end">Konec</option>
                      <option value="baseline">Základní řádek</option>
                    </Select>
                  </FormControl>

                  {/* Zarovnání (horizontálně) */}
                  <FormControl>
                    <FormLabel fontSize="xs">Zarovnání prvků (horizontálně)</FormLabel>
                    <Select
                      size="sm"
                      value={styles.justifyItems}
                      onChange={(e) => updateStyle('justifyItems', e.target.value)}
                    >
                      <option value="stretch">Roztáhnout</option>
                      <option value="start">Začátek</option>
                      <option value="center">Střed</option>
                      <option value="end">Konec</option>
                    </Select>
                  </FormControl>
                </>
              )}
            </VStack>
          </TabPanel>

          {/* Záložka: Vlastní CSS */}
          <TabPanel p={0}>
            <CustomCSSEditor
              elementName={elementName}
              onCSSChange={(css) => updateStyle('customCSS', css)}
              currentCSS={styles.customCSS || ''}
              currentStyles={styles}
              theme={{ primary: clubTheme.primary, secondary: clubTheme.secondary, accent: (clubTheme as any).accent }}
            />
          </TabPanel>

          {/* Záložka: Admin odkazy */}
          <TabPanel>
            <ContextualAdminLinks elementName={elementName} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default VisualStylePanel;
